import {
  Assignment,
  AttachFile,
  Event,
  Launch,
  MedicalInformationTwoTone,
  StorageRounded,
  Message,
  OpenInNew,
  SystemUpdate,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  ListItem,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useMarkPushNotificationAsRead } from "../../hooks/usePushNotifications";
import { NotificationType, UserNotification } from "../../types/notification";
import MedicationTaskCard from "./MedicationTaskCard";

interface NotificationItemProps {
  notification: UserNotification;
  handleClose?: () => void;
}

interface NotificationAction {
  label: string;
  action: string | (() => void);
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "error" | "success" | "warning" | "info";
}

interface NotificationAttachment {
  name: string;
  url?: string;
  type?: string;
  size?: number;
}

export default function NotificationItem({
  notification,
  handleClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsReadMutation = useMarkPushNotificationAsRead();

  const handleMarkAsRead = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const handleLinkClick = (e: React.MouseEvent, link: string) => {
    e.stopPropagation();
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(link);
    }
    handleClose?.();
    // Don't mark as read when clicking links - only dismiss button does that
  };

  const handleActionClick = (
    e: React.MouseEvent,
    action: NotificationAction,
  ) => {
    e.stopPropagation();
    if (typeof action.action === "function") {
      action.action();
    } else {
      handleLinkClick(e, action.action);
    }
    handleClose?.();
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.ENCOUNTER:
        return "#6B46C1";
      case NotificationType.APPOINTMENT:
        return "info";
      case NotificationType.SYSTEM:
        return "warning";
      case NotificationType.MEDICAL_RECORD:
        return "#6B46C1";
      case NotificationType.MESSAGE:
        return "secondary";
      case NotificationType.DATA_UPDATE:
        return "default";
      default:
        return "default";
    }
  };

  // Helper function to wrap icons with consistent styling
  const createStyledIcon = (
    IconComponent: React.ElementType,
  ) => {
    const iconColor = notification.read ? "text.secondary" : "#6B46C1";

    return <IconComponent fontSize="small" sx={{ color: iconColor }} />;
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.ENCOUNTER:
        return createStyledIcon(MedicalInformationTwoTone);
      case NotificationType.APPOINTMENT:
        return createStyledIcon(Event);
      case NotificationType.SYSTEM:
        return createStyledIcon(SystemUpdate);
      case NotificationType.MEDICAL_RECORD:
        return createStyledIcon(Assignment);
      case NotificationType.MESSAGE:
        return createStyledIcon(Message);
      case NotificationType.DATA_UPDATE:
        return createStyledIcon(StorageRounded);
      default:
        return null;
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.meta?.created), {
    addSuffix: true,
  });

  // Extract data fields
  const data = notification.data || {};
  const link = data.link as string | undefined;
  const actions = (data.actions as NotificationAction[]) || [];
  const attachments = (data.attachments as NotificationAttachment[]) || [];
  const imageUrl = data.imageUrl as string | undefined;
  const badge = data.badge as string | undefined;

  // Medication administration task data
  const medicationName = data.medicationName as string | undefined;
  const medicationRequestId = data.medicationRequestId as string | undefined;
  const taskStatus = data.taskStatus as "pending" | "completed" | undefined;
  const nextDue = data.nextDue as string | undefined;
  const completedAt = data.completedAt as string | undefined;
  const encounterId = data.encounterId as string | undefined;

  // Check if this is a medication administration notification with task data
  const isMedicationTask =
    notification.type === NotificationType.MEDICAL_RECORD &&
    medicationName &&
    taskStatus;

  const typeColor = getTypeColor(notification.type).startsWith("#")
    ? getTypeColor(notification.type)
    : `${getTypeColor(notification.type)}.main`;
  const typeIcon = getTypeIcon(notification.type);

  return (
    <ListItem
      sx={{
        backgroundColor: "transparent",
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        "&:hover": {
          backgroundColor: alpha("#000000", 0.02),
        },
        py: 2,
        px: 2,
        cursor: link || actions.length > 0 ? "pointer" : "default",
        flexDirection: "column",
        alignItems: "stretch",
      }}
      onClick={link ? (e) => handleLinkClick(e, link) : undefined}
    >
      <Box display="flex" alignItems="flex-start" gap={2} width="100%">
        {/* Square Icon */}
        <Box
          sx={{
            width: 35,
            height: 35,
            minWidth: 35,
            bgcolor: notification.read ? "#f2f2f2" : "#E8E0F5",
            border: "1px solid #e1e2e4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "5px", // Square, not rounded
            flexShrink: 0,
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              // https://avatar.iran.liara.run/username?username=Scott+Wilson
              alt={notification.title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            typeIcon && (
              <Box
                sx={{
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {typeIcon}
              </Box>
            )
          )}
        </Box>

        {/* Content */}
        <Box flex={1} minWidth={0}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: notification.read ? 400 : 700,
                color: notification.read ? "text.secondary" : "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {notification.title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  backgroundColor: typeColor,
                  color: typeColor.startsWith("default") ? "text.primary" : "#ffffff",
                }}
              />
            )}
            {/* Colored dot indicator for unread notifications */}
            {!notification.read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: typeColor,
                  flexShrink: 0,
                  ml: "auto",
                }}
              />
            )}
          </Box>

          {/* Message */}
          <Typography
            variant="body2"
            sx={{
              color: notification.read ? "text.secondary" : "text.primary",
              mb: 1,
              wordBreak: "break-word",
              fontWeight: notification.read ? 400 : 500,
            }}
          >
            {notification.message}
          </Typography>

          {/* Medication Task Card */}
          {isMedicationTask && (
            <Box sx={{ mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
              <MedicationTaskCard
                medicationName={medicationName}
                status={taskStatus}
                dueTime={nextDue}
                completedAt={completedAt}
                onClick={
                  encounterId && medicationRequestId
                    ? () => {
                        navigate(
                          `/health/medication-administrations/${encounterId}/edit`,
                        );
                      }
                    : undefined
                }
              />
            </Box>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
              {attachments.map((attachment, index) => (
                <Chip
                  key={index}
                  icon={<AttachFile fontSize="small" />}
                  label={attachment.name}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (attachment.url) {
                      window.open(
                        attachment.url,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                  }}
                  sx={{ cursor: attachment.url ? "pointer" : "default" }}
                />
              ))}
            </Stack>
          )}

          {/* Action Buttons */}
          {actions.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1, flexWrap: "wrap" }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  size="small"
                  variant={action.variant || "outlined"}
                  onClick={(e) => handleActionClick(e, action)}
                  sx={{
                    textTransform: "none",
                    backgroundColor: action.color || typeColor,
                    boxShadow: "none",
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}

          {/* Link */}
          {link && !actions.length && (
            <MuiLink
              component="button"
              variant="body2"
              onClick={(e) => handleLinkClick(e, link)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              View details
              {link.startsWith("http") ? (
                <OpenInNew fontSize="small" />
              ) : (
                <Launch fontSize="small" />
              )}
            </MuiLink>
          )}

          {/* Footer */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              {timeAgo}
              {notification.sourceService && ` • ${notification.sourceService}`}
            </Typography>
            {/* Dismiss button - marks notification as read */}
            <IconButton
              size="small"
              onClick={handleMarkAsRead}
              disabled={notification.read}
              sx={{
                opacity: notification.read ? 0.4 : 0.6,
                color: "text.secondary",
                fontSize: "0.75rem",
                borderRadius: "4px",
                padding: "4px 8px",
                backgroundColor: "transparent",
                "&:hover": {
                  opacity: notification.read ? 0.4 : 1,
                  bgcolor: alpha("#000000", 0.04),
                },
              }}
            >
              Dismiss
            </IconButton>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
}
